import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, retry, finalize } from 'rxjs/operators';
import { FormGroup } from '@angular/forms';
import { CryptedService } from '@shared/services/crypted/crypted.service';
import { AppConstants } from '@core/constants';
import { LoadingQueueService } from './loading-queue.service';
import { CustomRendererService } from '@shared/services/custom-renderer.service';

@Injectable({
  providedIn: 'root'
})
export class ApiSecureService {

  constructor(
    private http: HttpClient,
    private cryptedService: CryptedService,
    private loadingQueueService: LoadingQueueService,
    private customRendererService: CustomRendererService
  ) { }

  /**
   * Converts a plain JavaScript object to FormData
   * 
   * @param object The object to convert
   * @param options Optional configuration options
   * @returns FormData object that can be used with securePostMultipart and similar methods
   */
  public objectToFormData(
    object: Record<string, any>,
    options: {
      excludeFields?: string[],   // Fields to exclude from the FormData
      fileFields?: Record<string, File | File[]> // Any file fields that should be added to the FormData
    } = {}
  ): FormData {
    // Default options
    const {
      excludeFields = [],
      fileFields = {}
    } = options;

    const formData = new FormData();

    // Helper function to add fields recursively
    const addFieldToFormData = (data: any, parentKey?: string) => {
      if (data === null || data === undefined) {
        return;
      }

      // Special handling for File and Blob objects
      if (data instanceof File || data instanceof Blob) {
        if (parentKey && !excludeFields.includes(parentKey)) {
          formData.append(parentKey, data);
        }
        return;
      }

      if (typeof data === 'object') {
        // Handle objects and arrays
        Object.keys(data).forEach(key => {
          // Skip excluded fields
          if (excludeFields.includes(parentKey ? `${parentKey}.${key}` : key)) {
            return;
          }

          const fullKey = parentKey ? `${parentKey}[${key}]` : key;
          addFieldToFormData(data[key], fullKey);
        });
      } else {
        // Handle primitive values
        if (parentKey && !excludeFields.includes(parentKey)) {
          formData.append(parentKey, data === null || data === undefined ? '' : String(data));
        }
      }
    };

    // Add object values
    addFieldToFormData(object);

    // Add file fields
    Object.entries(fileFields).forEach(([fieldName, fileValue]) => {
      // Skip if field is in excludeFields
      if (excludeFields.includes(fieldName)) {
        return;
      }

      if (Array.isArray(fileValue)) {
        // Handle multiple files for the same field
        fileValue.forEach(file => {
          formData.append(fieldName, file);
        });
      } else if (fileValue) {
        // Handle single file
        formData.append(fieldName, fileValue);
      }
    });

    return formData;
  }

  /**
   * Converts an Angular FormGroup to FormData for use with multipart requests
   * 
   * @param formGroup The Angular FormGroup to convert
   * @param options Optional configuration options
   * @returns FormData object that can be used with securePostMultipart and similar methods
   */
  public formGroupToFormData(
    formGroup: FormGroup,
    options: {
      includeDisabled?: boolean,  // Include disabled controls (default: false)
      excludeFields?: string[],   // Fields to exclude from the FormData
      fileFields?: Record<string, File | File[]> // Any file fields that should be added to the FormData
    } = {}
  ): FormData {
    // Extract the form value based on the includeDisabled option
    const { includeDisabled = false } = options;
    const formValue = includeDisabled ? formGroup.getRawValue() : formGroup.value;

    // Delegate to objectToFormData for the actual conversion
    return this.objectToFormData(formValue, options);
  }

  /**
   * Secure GET request that handles decryption of the response
   * 
   * @param url The endpoint URL to make the GET request to (including any query parameters)
   * @returns Observable of the decrypted response
   */
  public secureGet<T>(url: string, showLoading = true): Observable<T> {
    // Create options object with credentials and observe response
    const options = {
      withCredentials: true,
      observe: 'response' as const
    };

    if (showLoading) {
      this.loadingQueueService.enqueueApiCall(url);
      this.customRendererService.show(AppConstants.MAT_SPINNER_CLASS);
    }

    return this.http.get<any>(AppConstants.API_HOST + url, options).pipe(
      retry(1),
      map(response => this.decryptResponse<T>(response)),
      catchError(error => {
        this.customRendererService.hide(AppConstants.MAT_SPINNER_CLASS);
        this.loadingQueueService.clearQueue();

        // Try to decrypt error response if possible
        if (error.error && error.error.crypted) {
          try {
            const decryptedError = this.cryptedService.decryptData(
              error.error.crypted,
              error.headers?.get('x-verify')
            );
            error.decryptedError = decryptedError;
          } catch (e) {
            // Ignore decryption errors on error responses
          }
        }
        throw error;
      }),
      finalize((): void => {
        if (showLoading) {
          const loadingQueueLength = this.loadingQueueService.dequeApiCall(url);
          if (loadingQueueLength < 1) {
            this.loadingQueueService.clearQueue();
            this.customRendererService.hide(AppConstants.MAT_SPINNER_CLASS);
          }
        }
      })
    );
  }

  /**
   * Secure POST request with JSON data
   */
  public securePost<T>(url: string, data: any, include?: string[], exclude?: string[]): Observable<T> {
    const encryptedRequest = this.encryptRequest(data, include, exclude);

    return this.http.post<any>(AppConstants.API_HOST + url, encryptedRequest.body, {
      headers: encryptedRequest.headers,
      withCredentials: true,
      observe: 'response'
    }).pipe(
      map(response => this.decryptResponse<T>(response)),
      catchError(error => {
        // Try to decrypt error response if possible
        if (error.error && error.error.crypted) {
          try {
            const decryptedError = this.cryptedService.decryptData(
              error.error.crypted,
              error.headers?.get('x-verify')
            );
            error.decryptedError = decryptedError;
          } catch (e) {
            // Ignore decryption errors on error responses
          }
        }
        throw error;
      })
    );
  }

  /**
   * Secure PUT request with JSON data
   */
  public securePut<T>(url: string, data: any, include?: string[], exclude?: string[]): Observable<T> {
    const encryptedRequest = this.encryptRequest(data, include, exclude);

    return this.http.put<any>(AppConstants.API_HOST + url, encryptedRequest.body, {
      headers: encryptedRequest.headers,
      withCredentials: true,
      observe: 'response'
    }).pipe(
      map(response => this.decryptResponse<T>(response)),
      catchError(error => {
        if (error.error && error.error.crypted) {
          try {
            error.decryptedError = this.cryptedService.decryptData(
              error.error.crypted,
              error.headers?.get('x-verify')
            );
          } catch (e) {
            // Ignore
          }
        }
        throw error;
      })
    );
  }

  /**
   * Secure PATCH request with JSON data
   */
  public securePatch<T>(url: string, data: any, include?: string[], exclude?: string[]): Observable<T> {
    const encryptedRequest = this.encryptRequest(data, include, exclude);

    return this.http.patch<any>(AppConstants.API_HOST + url, encryptedRequest.body, {
      headers: encryptedRequest.headers,
      withCredentials: true,
      observe: 'response'
    }).pipe(
      map(response => this.decryptResponse<T>(response)),
      catchError(error => {
        if (error.error && error.error.crypted) {
          try {
            error.decryptedError = this.cryptedService.decryptData(
              error.error.crypted,
              error.headers?.get('x-verify')
            );
          } catch (e) {
            // Ignore
          }
        }
        throw error;
      })
    );
  }

  /**
   * Secure POST request with multipart form data
   */
  public securePostMultipart<T>(url: string, formData: FormData, include?: string[], exclude?: string[], showLoading = true): Observable<T> {
    const encryptedRequest = this.encryptMultipartRequest(formData, include, exclude);

    if (showLoading) {
      this.loadingQueueService.enqueueApiCall(url);
      this.customRendererService.show(AppConstants.MAT_SPINNER_CLASS);
    }

    return this.http.post<any>(AppConstants.API_HOST + url, encryptedRequest.body, {
      headers: encryptedRequest.headers,
      withCredentials: true,
      observe: 'response'
    }).pipe(
      map(response => this.decryptResponse<T>(response)),
      catchError(error => {
        this.customRendererService.hide(AppConstants.MAT_SPINNER_CLASS);
        this.loadingQueueService.clearQueue();

        if (error.error && error.error.crypted) {
          try {
            error.decryptedError = this.cryptedService.decryptData(
              error.error.crypted,
              error.headers?.get('x-verify')
            );
          } catch (e) {
            // Ignore
          }
        }
        throw error;
      }),
      finalize((): void => {
        if (showLoading) {
          const loadingQueueLength = this.loadingQueueService.dequeApiCall(url);
          if (loadingQueueLength < 1) {
            this.loadingQueueService.clearQueue();
            this.customRendererService.hide(AppConstants.MAT_SPINNER_CLASS);
          }
        }
      })
    );
  }

  /**
   * Secure PUT request with multipart form data
   */
  public securePutMultipart<T>(url: string, formData: FormData, include?: string[], exclude?: string[]): Observable<T> {
    const encryptedRequest = this.encryptMultipartRequest(formData, include, exclude);

    return this.http.put<any>(AppConstants.API_HOST + url, encryptedRequest.body, {
      headers: encryptedRequest.headers,
      withCredentials: true,
      observe: 'response'
    }).pipe(
      map(response => this.decryptResponse<T>(response)),
      catchError(error => {
        if (error.error && error.error.crypted) {
          try {
            error.decryptedError = this.cryptedService.decryptData(
              error.error.crypted,
              error.headers?.get('x-verify')
            );
          } catch (e) {
            // Ignore
          }
        }
        throw error;
      })
    );
  }

  /**
   * Secure PATCH request with multipart form data
   */
  public securePatchMultipart<T>(url: string, formData: FormData, include?: string[], exclude?: string[]): Observable<T> {
    const encryptedRequest = this.encryptMultipartRequest(formData, include, exclude);

    return this.http.patch<any>(AppConstants.API_HOST + url, encryptedRequest.body, {
      headers: encryptedRequest.headers,
      withCredentials: true,
      observe: 'response'
    }).pipe(
      map(response => this.decryptResponse<T>(response)),
      catchError(error => {
        if (error.error && error.error.crypted) {
          try {
            error.decryptedError = this.cryptedService.decryptData(
              error.error.crypted,
              error.headers?.get('x-verify')
            );
          } catch (e) {
            // Ignore
          }
        }
        throw error;
      })
    );
  }

  /**
   * Encrypts a JSON request based on include/exclude lists
   */
  private encryptRequest(
    data: any,
    include?: string[],
    exclude?: string[]
  ): { body: any, headers: HttpHeaders } {
    // Create the object to be encrypted
    const dataToEncrypt: Record<string, any> = {};
    const fieldsToKeepUnencrypted: Record<string, any> = {};

    // Process each property in the data object
    Object.keys(data).forEach(key => {
      // Determine if this field should be encrypted
      let shouldEncrypt = true;

      // If include list is defined and non-empty, only encrypt fields in that list
      if (include && include.length > 0) {
        shouldEncrypt = include.includes(key);
      }

      // If exclude list is defined and non-empty, don't encrypt fields in that list
      if (exclude && exclude.length > 0) {
        if (exclude.includes(key)) {
          shouldEncrypt = false;
        }
      }

      if (shouldEncrypt) {
        dataToEncrypt[key] = data[key];
      } else {
        fieldsToKeepUnencrypted[key] = data[key];
      }
    });

    // Uses synchronous CryptedService
    const result = this.cryptedService.encryptData(dataToEncrypt);

    // Create the body with the encrypted data and unencrypted fields
    const body = {
      crypted: result.encrypted,
      ...fieldsToKeepUnencrypted
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-verify': result.hmac
    });

    return { body, headers };
  }

  /**
   * Encrypts a multipart form data request by extracting form values,
   * encrypting them, and adding them back to the form based on include/exclude lists
   */
  private encryptMultipartRequest(
    formData: FormData,
    include?: string[],
    exclude?: string[]
  ): { body: FormData, headers: HttpHeaders } {
    // Extract form data into a plain object (excluding files)
    const formObject: Record<string, any> = {};
    const files: Record<string, File> = {};

    // First, collect all files and form values
    formData.forEach((value, key) => {
      if (value instanceof File) {
        // Store file references separately
        files[key] = value;
      } else {
        // Determine if this field should be encrypted
        let shouldEncrypt = true;

        // If include list is defined and non-empty, only encrypt fields in that list
        if (include && include.length > 0) {
          shouldEncrypt = include.includes(key);
        }

        // If exclude list is defined and non-empty, don't encrypt fields in that list
        if (exclude && exclude.length > 0) {
          if (exclude.includes(key)) {
            shouldEncrypt = false;
          }
        }

        if (shouldEncrypt) {
          formObject[key] = value;
        }
      }
    });

    // Uses synchronous CryptedService
    const result = this.cryptedService.encryptData(formObject);

    // Create a new FormData object
    const encryptedFormData = new FormData();

    // Add the encrypted data
    encryptedFormData.append('crypted', result.encrypted);

    // Add unencrypted fields
    formData.forEach((value, key) => {
      // For files, directly append the File object
      if (key in files) {
        encryptedFormData.append(key, files[key]);
      }
      // For fields that should not be encrypted
      else if (
        (include && include.length > 0 && !include.includes(key)) ||
        (exclude && exclude.length > 0 && exclude.includes(key))
      ) {
        // Check if it's an array-like structure
        if (formData.getAll(key).length > 1) {
          // If the field has multiple values, append each one
          formData.getAll(key).forEach(item => {
            encryptedFormData.append(key, item);
          });
        } else {
          encryptedFormData.append(key, value);
        }
      }
      // Note: encrypted fields are already handled in the 'crypted' value
    });

    // Don't set Content-Type header - browser will set it with boundary
    const headers = new HttpHeaders({
      'x-verify': result.hmac
    });

    return { body: encryptedFormData, headers };
  }

  /**
   * Decrypts the response and merges with unencrypted fields
   * Handles three types of responses:
   * 1. Single object responses
   * 2. List of objects responses
   * 3. Paginated responses with a 'results' field containing a list of objects
   */
  private decryptResponse<T>(response: any): T {
    if (!response || !response.body) {
      throw new Error('Invalid response structure');
    }

    // Helper function to decrypt a single object
    const decryptSingleObject = (obj: any, hmac: string | undefined, hmac_data?: Record<string, any>): any => {
      // If there's no crypted field, return the object as is
      if (!obj.crypted) {
        return obj;
      }

      const encryptedData = obj.crypted;

      // Uses synchronous CryptedService to decrypt the data
      const decryptedData = this.cryptedService.decryptData(encryptedData, hmac, hmac_data);

      // Create a copy of the object without the 'crypted' field
      const { crypted, ...unencryptedFields } = obj;

      // Merge the decrypted data with any unencrypted fields
      return {
        ...decryptedData,
        ...unencryptedFields
      };
    };

    const hmac = response.headers?.get('x-verify');
    const responseBody = response.body;

    // Case 1: Check if it's a paginated response with 'results' field
    if (responseBody && typeof responseBody === 'object' && 'results' in responseBody && Array.isArray(responseBody.results)) {
      // Make a copy of the response to avoid modifying the original
      const decryptedResponse = { ...responseBody };

      // Decrypt each item in the results array
      const hmac_data = {count: responseBody.results.length}
      decryptedResponse.results = responseBody.results.map((item: any) => decryptSingleObject(item, hmac, hmac_data));

      return decryptedResponse as T;
    }

    // Case 2: Check if it's an array of objects
    else if (Array.isArray(responseBody)) {
      // Decrypt each item in the array
      const hmac_data = {count: responseBody.length}
      return responseBody.map(item => decryptSingleObject(item, hmac, hmac_data)) as unknown as T;
    }

    // Case 3: It's a single object
    else {
      return decryptSingleObject(responseBody, hmac) as T;
    }
  }
}